import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { scansAPI } from '../../services/api';
import { Scan, ScanResult } from '../../types';
import './ScanComparison.css';

interface ScanComparisonProps {
  projectId: number;
  scanIds: number[];
  onClose: () => void;
}

// Backend API response interfaces matching the Go structs
interface ComparisonData {
  comparison_id: string;
  generated_at: string;
  scan_count: number;
  scans: Scan[];
  target_analysis: TargetAnalysis;
  severity_analysis: SeverityAnalysis;
  vuln_type_analysis: VulnTypeAnalysis;
  timeline_analysis: TimelineAnalysis;
  vuln_diff_analysis: VulnDiffAnalysis;
  conclusions: string[];
}

interface TargetAnalysis {
  common_targets: string[];
  unique_targets: { [scanId: string]: string[] };
  target_metrics: { [target: string]: TargetMetrics };
  overlap_matrix: number[][];
}

interface TargetMetrics {
  total_vulnerabilities: number;
  severity_breakdown: SeverityBreakdown;
  scan_presence: number[];
}

interface SeverityBreakdown {
  critical: number;
  high: number;
  medium: number;
  low: number;
  info: number;
}

interface SeverityAnalysis {
  severity_trends: { [scanId: string]: SeverityBreakdown };
  severity_by_target: { [target: string]: { [scanId: string]: SeverityBreakdown } };
  total_by_severity: SeverityBreakdown;
  severity_evolution: SeverityEvolution[];
}

interface SeverityEvolution {
  scan_id: number;
  scan_name: string;
  date: string;
  severity_counts: SeverityBreakdown;
}

interface VulnTypeAnalysis {
  type_distribution: { [scanId: string]: { [type: string]: number } };
  type_by_target: { [target: string]: { [scanId: string]: { [type: string]: number } } };
  common_types: string[];
  unique_types: { [scanId: string]: string[] };
  type_evolution: TypeEvolution[];
}

interface TypeEvolution {
  scan_id: number;
  scan_name: string;
  date: string;
  type_counts: { [type: string]: number };
}

interface TimelineAnalysis {
  scan_timeline: ScanTimelinePoint[];
  vuln_progression: VulnProgression[];
  target_progression: TargetProgression[];
}

// Vulnerability diff types
interface VulnDiffAnalysis {
  new_vulnerabilities: VulnDiffItem[];
  resolved_vulnerabilities: VulnDiffItem[];
  persistent_vulnerabilities: VulnDiffItem[];
  summary: VulnDiffSummary;
}

interface VulnDiffItem {
  title: string;
  host: string;
  port: number;
  severity: string;
  vuln_type: string;
  fingerprint: string;
  scan_id: number;
  scan_name: string;
}

interface VulnDiffSummary {
  total_new: number;
  total_resolved: number;
  total_persistent: number;
  new_by_severity: { [key: string]: number };
  resolved_by_severity: { [key: string]: number };
  trend_direction: string;
}

interface ScanTimelinePoint {
  scan_id: number;
  scan_name: string;
  date: string;
  total_vulnerabilities: number;
  target_count: number;
}

interface VulnProgression {
  date: string;
  severity_counts: SeverityBreakdown;
  total_count: number;
}

interface TargetProgression {
  date: string;
  new_targets: string[];
  lost_targets: string[];
  total_targets: number;
}

// Helper function to filter out system severity findings from comparison data
const filterOutSystemFindings = (data: ComparisonData): ComparisonData => {
  // Filter vuln_diff_analysis to exclude system severity
  const filteredVulnDiff: VulnDiffAnalysis = {
    new_vulnerabilities: (data.vuln_diff_analysis?.new_vulnerabilities || []).filter(v => v.severity !== 'system'),
    resolved_vulnerabilities: (data.vuln_diff_analysis?.resolved_vulnerabilities || []).filter(v => v.severity !== 'system'),
    persistent_vulnerabilities: (data.vuln_diff_analysis?.persistent_vulnerabilities || []).filter(v => v.severity !== 'system'),
    summary: {
      ...data.vuln_diff_analysis?.summary,
      total_new: (data.vuln_diff_analysis?.new_vulnerabilities || []).filter(v => v.severity !== 'system').length,
      total_resolved: (data.vuln_diff_analysis?.resolved_vulnerabilities || []).filter(v => v.severity !== 'system').length,
      total_persistent: (data.vuln_diff_analysis?.persistent_vulnerabilities || []).filter(v => v.severity !== 'system').length,
      // Recalculate new/resolved by severity excluding system
      new_by_severity: Object.fromEntries(
        Object.entries(data.vuln_diff_analysis?.summary?.new_by_severity || {}).filter(([key]) => key !== 'system')
      ),
      resolved_by_severity: Object.fromEntries(
        Object.entries(data.vuln_diff_analysis?.summary?.resolved_by_severity || {}).filter(([key]) => key !== 'system')
      ),
      trend_direction: data.vuln_diff_analysis?.summary?.trend_direction || 'stable'
    }
  };

  return {
    ...data,
    vuln_diff_analysis: filteredVulnDiff
  };
};

const ScanComparison: React.FC<ScanComparisonProps> = ({ projectId, scanIds, onClose }) => {
  const { i18n } = useTranslation();
  const [comparisonData, setComparisonData] = useState<ComparisonData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reportHtml, setReportHtml] = useState<string>('');
  const [showPreview, setShowPreview] = useState(false);
  // AI narrative for a two-scan comparison (works for manual & any pair now)
  const [aiText, setAiText] = useState<string>('');
  const [aiStatus, setAiStatus] = useState<'idle' | 'loading' | 'generating' | 'ready' | 'unavailable'>('idle');

  useEffect(() => {
    loadComparisonData();
    loadAIComparison();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scanIds]);

  // Pick the localized comparison text from an AIScanComparison record.
  const pickAIText = (rec: any): string => {
    const lang = (i18n.language || 'en').slice(0, 2);
    return rec?.[`comparison_${lang}`] || rec?.comparison_en || rec?.comparison_de || '';
  };

  // Fetch (or generate on demand) the AI comparison narrative for exactly two
  // selected scans — the newer scan compared against the older one.
  const loadAIComparison = async () => {
    if (!scanIds || scanIds.length !== 2) {
      setAiStatus('idle');
      return;
    }
    const newer = Math.max(scanIds[0], scanIds[1]);
    const older = Math.min(scanIds[0], scanIds[1]);
    setAiStatus('loading');
    try {
      const existing = await scansAPI.getAIComparison(newer, older);
      const txt = pickAIText(existing.data);
      if (txt) { setAiText(txt); setAiStatus('ready'); return; }
    } catch (e) { /* 404 → not generated yet, fall through to generate */ }

    try {
      setAiStatus('generating');
      await scansAPI.generateAIComparison(newer, older, { async: true });
      // Poll for completion (AI generation runs in the background).
      for (let i = 0; i < 20; i++) {
        await new Promise(r => setTimeout(r, 3000));
        try {
          const rec = await scansAPI.getAIComparison(newer, older);
          const txt = pickAIText(rec.data);
          if (txt) { setAiText(txt); setAiStatus('ready'); return; }
        } catch (e) { /* still generating */ }
      }
      setAiStatus('unavailable');
    } catch (e) {
      // AI disabled for this scan/client, or generation failed.
      setAiStatus('unavailable');
    }
  };

  const loadComparisonData = async () => {
    try {
      setLoading(true);
      setError(null);

      console.log('Starting comparison data load for scan IDs:', scanIds);

      // Fetch comparison data from backend
      const response = await scansAPI.compareScans(scanIds);
      console.log('API response received:', response);
      console.log('Response data structure:', response.data);

      // Filter out system severity findings - they make no sense in comparisons
      const filteredData = filterOutSystemFindings(response.data);
      setComparisonData(filteredData);

      // Generate HTML report with filtered data
      console.log('Generating HTML report...');
      const htmlReport = await generateHtmlReport(filteredData);
      console.log('HTML report generated successfully');
      setReportHtml(htmlReport);
    } catch (err) {
      console.error('Failed to load comparison data - detailed error:', err);
      console.error('Error stack:', err instanceof Error ? err.stack : 'No stack trace');
      setError(`Failed to load comparison data: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  const generateHtmlReport = async (data: ComparisonData): Promise<string> => {
    // Debug the data structure
    console.log('Generating HTML report with data:', data);
    console.log('Scans data:', data.scans);
    console.log('Severity analysis:', data.severity_analysis);
    console.log('Target analysis:', data.target_analysis);
    
    // This will generate a comprehensive HTML report with charts
    const html = `
<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Scan Comparison Report</title>
    <script src="https://cdn.jsdelivr.net/npm/chart.js"></script>
    <style>
        body {
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
            line-height: 1.6;
            color: #333;
            max-width: 1200px;
            margin: 0 auto;
            padding: 20px;
            background: #f8fafc;
        }
        .header {
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            color: white;
            padding: 2rem;
            border-radius: 12px;
            margin-bottom: 2rem;
            text-align: center;
        }
        .header h1 {
            margin: 0;
            font-size: 2.5rem;
            font-weight: 300;
        }
        .header p {
            margin: 0.5rem 0 0 0;
            opacity: 0.9;
        }
        .section {
            background: white;
            border-radius: 12px;
            padding: 2rem;
            margin-bottom: 2rem;
            box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
        }
        .section h2 {
            color: #2d3748;
            border-bottom: 3px solid #667eea;
            padding-bottom: 0.5rem;
            margin-bottom: 1.5rem;
        }
        .chart-container {
            position: relative;
            height: 400px;
            margin: 2rem 0;
        }
        .stats-grid {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
            gap: 1rem;
            margin: 1.5rem 0;
        }
        .stat-card {
            background: #f7fafc;
            padding: 1.5rem;
            border-radius: 8px;
            text-align: center;
            border-left: 4px solid #667eea;
        }
        .stat-value {
            font-size: 2rem;
            font-weight: bold;
            color: #2d3748;
        }
        .stat-label {
            color: #718096;
            font-size: 0.875rem;
            margin-top: 0.5rem;
        }
        .conclusion {
            background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
            color: white;
            padding: 2rem;
            border-radius: 12px;
            margin-top: 2rem;
        }
        .conclusion h2 {
            color: white;
            border-bottom: 2px solid rgba(255,255,255,0.3);
        }
        .scan-info {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
            gap: 1rem;
            margin-bottom: 2rem;
        }
        .scan-card {
            background: #edf2f7;
            padding: 1rem;
            border-radius: 8px;
            border-left: 4px solid #4299e1;
        }
        .severity-critical { color: #e53e3e; }
        .severity-high { color: #dd6b20; }
        .severity-medium { color: #d69e2e; }
        .severity-low { color: #38a169; }
        .severity-info { color: #3182ce; }
        table {
            width: 100%;
            border-collapse: collapse;
            margin: 1rem 0;
        }
        th, td {
            padding: 0.75rem;
            text-align: left;
            border-bottom: 1px solid #e2e8f0;
        }
        th {
            background: #f7fafc;
            font-weight: 600;
        }
        .progress-bar {
            background: #e2e8f0;
            border-radius: 4px;
            overflow: hidden;
            height: 8px;
            margin: 0.5rem 0;
        }
        .progress-fill {
            height: 100%;
            background: linear-gradient(90deg, #667eea, #764ba2);
            transition: width 0.3s ease;
        }
        .spinner {
            width: 40px;
            height: 40px;
            border: 4px solid #f3f4f6;
            border-top: 4px solid #3b82f6;
            border-radius: 50%;
            animation: spin 1s linear infinite;
        }
        @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
        }
        .error-icon {
            width: 40px;
            height: 40px;
            background: #ef4444;
            color: white;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 24px;
            font-weight: bold;
        }
    </style>
</head>
<body>
    <div class="header">
        <h1>Scan Comparison Report</h1>
        <p>Comprehensive analysis of ${data.scans.length} security scans</p>
        <p>Generated on ${new Date().toLocaleDateString()}</p>
    </div>

    <div class="section">
        <h2>Executive Summary</h2>
        <div class="scan-info">
            ${data.scans.map(scan => `
                <div class="scan-card">
                    <h3>${scan.name}</h3>
                    <p><strong>Date:</strong> ${new Date(scan.created_at).toLocaleDateString()}</p>
                    <p><strong>Status:</strong> ${scan.status}</p>
                    <p><strong>Results:</strong> ${(scan as any).result_count || 0} findings</p>
                </div>
            `).join('')}
        </div>
    </div>

    <div class="section">
        <h2>Target Analysis</h2>
        <div class="chart-container">
            <canvas id="targetChart"></canvas>
        </div>
        <div class="stats-grid">
            ${data.scans.map(scan => {
                // Use target_count from scan object directly
                const targetCount = (scan as any).target_count || 0;
                console.log('Target count for scan', scan.name, ':', targetCount);
                return `
                    <div class="stat-card">
                        <div class="stat-value">${targetCount}</div>
                        <div class="stat-label">${scan.name} Targets</div>
                    </div>
                `;
            }).join('')}
            <div class="stat-card">
                <div class="stat-value">${data.target_analysis?.common_targets?.length || 0}</div>
                <div class="stat-label">Common Targets</div>
            </div>
        </div>
    </div>

    <div class="section">
        <h2>Vulnerability Severity Analysis</h2>
        <div class="chart-container">
            <canvas id="severityChart"></canvas>
        </div>
        <div class="chart-container">
            <canvas id="severityRadarChart"></canvas>
        </div>
    </div>

    <div class="section">
        <h2>Timeline Evolution</h2>
        <div class="chart-container">
            <canvas id="timelineChart"></canvas>
        </div>
        <div class="chart-container">
            <canvas id="progressionChart"></canvas>
        </div>
    </div>

    <div class="section">
        <h2>Vulnerability Type Distribution</h2>
        <div class="chart-container">
            <canvas id="vulnTypeChart"></canvas>
        </div>
        <div class="chart-container">
            <canvas id="vulnTypeScatterChart"></canvas>
        </div>
    </div>

    <div class="section">
        <h2>Detailed Comparison Table</h2>
        <table>
            <thead>
                <tr>
                    <th>Metric</th>
                    ${data.scans.map(scan => `<th>${scan.name}</th>`).join('')}
                </tr>
            </thead>
            <tbody>
                <tr>
                    <td><strong>Total Vulnerabilities</strong></td>
                    ${data.scans.map((scan, index) => {
                        // Use the result_count from the scan object directly
                        const total = (scan as any).result_count || 0;
                        console.log('Using scan result_count for scan:', scan.name, 'Total:', total);
                        return `<td>${total}</td>`;
                    }).join('')}
                </tr>
                <tr>
                    <td><strong>Critical</strong></td>
                    ${data.scans.map(scan => {
                        // For now, use proportional distribution from total_by_severity
                        const totalVulns = data.severity_analysis?.total_by_severity;
                        const scanTotal = (scan as any).result_count || 0;
                        const totalAll = totalVulns ? Object.values(totalVulns).reduce((a, b) => a + b, 0) : 1;
                        const criticalRatio = (totalVulns?.critical || 0) / totalAll;
                        const estimated = Math.round(scanTotal * criticalRatio);
                        return `<td class="severity-critical">${estimated}</td>`;
                    }).join('')}
                </tr>
                <tr>
                    <td><strong>High</strong></td>
                    ${data.scans.map(scan => {
                        const totalVulns = data.severity_analysis?.total_by_severity;
                        const scanTotal = (scan as any).result_count || 0;
                        const totalAll = totalVulns ? Object.values(totalVulns).reduce((a, b) => a + b, 0) : 1;
                        const highRatio = (totalVulns?.high || 0) / totalAll;
                        const estimated = Math.round(scanTotal * highRatio);
                        return `<td class="severity-high">${estimated}</td>`;
                    }).join('')}
                </tr>
                <tr>
                    <td><strong>Medium</strong></td>
                    ${data.scans.map(scan => {
                        const totalVulns = data.severity_analysis?.total_by_severity;
                        const scanTotal = (scan as any).result_count || 0;
                        const totalAll = totalVulns ? Object.values(totalVulns).reduce((a, b) => a + b, 0) : 1;
                        const mediumRatio = (totalVulns?.medium || 0) / totalAll;
                        const estimated = Math.round(scanTotal * mediumRatio);
                        return `<td class="severity-medium">${estimated}</td>`;
                    }).join('')}
                </tr>
                <tr>
                    <td><strong>Low</strong></td>
                    ${data.scans.map(scan => {
                        const totalVulns = data.severity_analysis?.total_by_severity;
                        const scanTotal = (scan as any).result_count || 0;
                        const totalAll = totalVulns ? Object.values(totalVulns).reduce((a, b) => a + b, 0) : 1;
                        const lowRatio = (totalVulns?.low || 0) / totalAll;
                        const estimated = Math.round(scanTotal * lowRatio);
                        return `<td class="severity-low">${estimated}</td>`;
                    }).join('')}
                </tr>
                <tr>
                    <td><strong>Info</strong></td>
                    ${data.scans.map(scan => {
                        const totalVulns = data.severity_analysis?.total_by_severity;
                        const scanTotal = (scan as any).result_count || 0;
                        const totalAll = totalVulns ? Object.values(totalVulns).reduce((a, b) => a + b, 0) : 1;
                        const infoRatio = (totalVulns?.info || 0) / totalAll;
                        const estimated = Math.round(scanTotal * infoRatio);
                        return `<td class="severity-info">${estimated}</td>`;
                    }).join('')}
                </tr>
            </tbody>
        </table>
    </div>

    <div class="conclusion">
        <h2>Analysis Conclusions</h2>
        ${(data.conclusions || []).map(conclusion => `<p>• ${conclusion}</p>`).join('')}
    </div>

    <script>
        // Chart.js configurations and data rendering
        ${generateChartScripts(data)}
    </script>
</body>
</html>`;
    
    return html;
  };

  const generateChartScripts = (data: ComparisonData): string => {
    return `
        // Target Comparison Chart
        const targetCtx = document.getElementById('targetChart').getContext('2d');
        new Chart(targetCtx, {
            type: 'bar',
            data: {
                labels: [${data.scans.map(scan => `'${scan.name}'`).join(', ')}],
                datasets: [{
                    label: 'Total Targets',
                    data: [${data.scans.map(scan => {
                        // Use target_count from scan object directly - console shows this exists
                        const targetCount = (scan as any).target_count || 1;
                        console.log('Target count for scan', scan.name, ':', targetCount);
                        return targetCount;
                    }).join(', ')}],
                    backgroundColor: 'rgba(102, 126, 234, 0.8)',
                    borderColor: 'rgba(102, 126, 234, 1)',
                    borderWidth: 1
                }]
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                    title: {
                        display: true,
                        text: 'Target Count Comparison'
                    }
                }
            }
        });

        // Severity Comparison Chart
        const severityCtx = document.getElementById('severityChart').getContext('2d');
        new Chart(severityCtx, {
            type: 'bar',
            data: {
                labels: [${data.scans.map(scan => `'${scan.name}'`).join(', ')}],
                datasets: [
                    {
                        label: 'Critical',
                        data: [${data.scans.map(scan => {
                            const totalVulns = data.severity_analysis?.total_by_severity;
                            const scanTotal = (scan as any).result_count || 0;
                            const totalAll = totalVulns ? Object.values(totalVulns).reduce((a, b) => a + b, 0) : 1;
                            const criticalRatio = (totalVulns?.critical || 0) / totalAll;
                            return Math.round(scanTotal * criticalRatio);
                        }).join(', ')}],
                        backgroundColor: 'rgba(229, 62, 62, 0.8)'
                    },
                    {
                        label: 'High',
                        data: [${data.scans.map(scan => {
                            const totalVulns = data.severity_analysis?.total_by_severity;
                            const scanTotal = (scan as any).result_count || 0;
                            const totalAll = totalVulns ? Object.values(totalVulns).reduce((a, b) => a + b, 0) : 1;
                            const highRatio = (totalVulns?.high || 0) / totalAll;
                            return Math.round(scanTotal * highRatio);
                        }).join(', ')}],
                        backgroundColor: 'rgba(221, 107, 32, 0.8)'
                    },
                    {
                        label: 'Medium',
                        data: [${data.scans.map(scan => {
                            const totalVulns = data.severity_analysis?.total_by_severity;
                            const scanTotal = (scan as any).result_count || 0;
                            const totalAll = totalVulns ? Object.values(totalVulns).reduce((a, b) => a + b, 0) : 1;
                            const mediumRatio = (totalVulns?.medium || 0) / totalAll;
                            return Math.round(scanTotal * mediumRatio);
                        }).join(', ')}],
                        backgroundColor: 'rgba(214, 158, 46, 0.8)'
                    },
                    {
                        label: 'Low',
                        data: [${data.scans.map(scan => {
                            const totalVulns = data.severity_analysis?.total_by_severity;
                            const scanTotal = (scan as any).result_count || 0;
                            const totalAll = totalVulns ? Object.values(totalVulns).reduce((a, b) => a + b, 0) : 1;
                            const lowRatio = (totalVulns?.low || 0) / totalAll;
                            return Math.round(scanTotal * lowRatio);
                        }).join(', ')}],
                        backgroundColor: 'rgba(56, 161, 105, 0.8)'
                    },
                    {
                        label: 'Info',
                        data: [${data.scans.map(scan => {
                            const totalVulns = data.severity_analysis?.total_by_severity;
                            const scanTotal = (scan as any).result_count || 0;
                            const totalAll = totalVulns ? Object.values(totalVulns).reduce((a, b) => a + b, 0) : 1;
                            const infoRatio = (totalVulns?.info || 0) / totalAll;
                            return Math.round(scanTotal * infoRatio);
                        }).join(', ')}],
                        backgroundColor: 'rgba(49, 130, 206, 0.8)'
                    }
                ]
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                    title: {
                        display: true,
                        text: 'Vulnerability Severity Comparison'
                    }
                },
                scales: {
                    x: {
                        stacked: true
                    },
                    y: {
                        stacked: true
                    }
                }
            }
        });

        // Severity Radar Chart
        const radarCtx = document.getElementById('severityRadarChart').getContext('2d');
        new Chart(radarCtx, {
            type: 'radar',
            data: {
                labels: ['Critical', 'High', 'Medium', 'Low', 'Info'],
                datasets: [
                    ${data.scans.map((scan, index) => {
                        const totalVulns = data.severity_analysis?.total_by_severity;
                        const scanTotal = (scan as any).result_count || 0;
                        const totalAll = totalVulns ? Object.values(totalVulns).reduce((a, b) => a + b, 0) : 1;
                        return `{
                            label: '${scan.name}',
                            data: [
                                ${Math.round(scanTotal * ((totalVulns?.critical || 0) / totalAll))},
                                ${Math.round(scanTotal * ((totalVulns?.high || 0) / totalAll))},
                                ${Math.round(scanTotal * ((totalVulns?.medium || 0) / totalAll))},
                                ${Math.round(scanTotal * ((totalVulns?.low || 0) / totalAll))},
                                ${Math.round(scanTotal * ((totalVulns?.info || 0) / totalAll))}
                            ],
                            backgroundColor: 'rgba(${102 + index * 50}, ${126 + index * 30}, ${234 - index * 40}, 0.2)',
                            borderColor: 'rgba(${102 + index * 50}, ${126 + index * 30}, ${234 - index * 40}, 1)',
                            borderWidth: 2
                        }`;
                    }).join(', ')}
                ]
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                    title: {
                        display: true,
                        text: 'Severity Distribution Radar'
                    }
                }
            }
        });

        // Timeline Chart
        const timelineCtx = document.getElementById('timelineChart').getContext('2d');
        new Chart(timelineCtx, {
            type: 'line',
            data: {
                labels: [${data.scans.map(scan => `'${scan.name}'`).join(', ')}],
                datasets: [
                    {
                        label: 'Total Vulnerabilities',
                        data: [${data.scans.map(scan => (scan as any).result_count || 0).join(', ')}],
                        borderColor: 'rgba(102, 126, 234, 1)',
                        backgroundColor: 'rgba(102, 126, 234, 0.1)',
                        fill: true,
                        tension: 0.4
                    },
                    {
                        label: 'Critical',
                        data: [${data.scans.map(scan => {
                            const totalVulns = data.severity_analysis?.total_by_severity;
                            const scanTotal = (scan as any).result_count || 0;
                            const totalAll = totalVulns ? Object.values(totalVulns).reduce((a, b) => a + b, 0) : 1;
                            const criticalRatio = (totalVulns?.critical || 0) / totalAll;
                            return Math.round(scanTotal * criticalRatio);
                        }).join(', ')}],
                        borderColor: 'rgba(229, 62, 62, 1)',
                        backgroundColor: 'rgba(229, 62, 62, 0.1)',
                        fill: false
                    },
                    {
                        label: 'High',
                        data: [${data.scans.map(scan => {
                            const totalVulns = data.severity_analysis?.total_by_severity;
                            const scanTotal = (scan as any).result_count || 0;
                            const totalAll = totalVulns ? Object.values(totalVulns).reduce((a, b) => a + b, 0) : 1;
                            const highRatio = (totalVulns?.high || 0) / totalAll;
                            return Math.round(scanTotal * highRatio);
                        }).join(', ')}],
                        borderColor: 'rgba(221, 107, 32, 1)',
                        backgroundColor: 'rgba(221, 107, 32, 0.1)',
                        fill: false
                    },
                    {
                        label: 'Medium',
                        data: [${data.scans.map(scan => {
                            const totalVulns = data.severity_analysis?.total_by_severity;
                            const scanTotal = (scan as any).result_count || 0;
                            const totalAll = totalVulns ? Object.values(totalVulns).reduce((a, b) => a + b, 0) : 1;
                            const mediumRatio = (totalVulns?.medium || 0) / totalAll;
                            return Math.round(scanTotal * mediumRatio);
                        }).join(', ')}],
                        borderColor: 'rgba(214, 158, 46, 1)',
                        backgroundColor: 'rgba(214, 158, 46, 0.1)',
                        fill: false
                    },
                    {
                        label: 'Low',
                        data: [${data.scans.map(scan => {
                            const totalVulns = data.severity_analysis?.total_by_severity;
                            const scanTotal = (scan as any).result_count || 0;
                            const totalAll = totalVulns ? Object.values(totalVulns).reduce((a, b) => a + b, 0) : 1;
                            const lowRatio = (totalVulns?.low || 0) / totalAll;
                            return Math.round(scanTotal * lowRatio);
                        }).join(', ')}],
                        borderColor: 'rgba(56, 161, 105, 1)',
                        backgroundColor: 'rgba(56, 161, 105, 0.1)',
                        fill: false
                    }
                ]
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                    title: {
                        display: true,
                        text: 'Vulnerability Timeline Evolution'
                    }
                },
                scales: {
                    y: {
                        beginAtZero: true
                    }
                }
            }
        });

        // Vulnerability Severity Distribution (more meaningful than type)
        const vulnTypeCtx = document.getElementById('vulnTypeChart').getContext('2d');
        const severityData = ${JSON.stringify(data.severity_analysis?.total_by_severity || {})};
        
        new Chart(vulnTypeCtx, {
            type: 'doughnut',
            data: {
                labels: ['Critical', 'High', 'Medium', 'Low', 'Info'],
                datasets: [{
                    data: [
                        severityData.critical || 0,
                        severityData.high || 0,
                        severityData.medium || 0,
                        severityData.low || 0,
                        severityData.info || 0
                    ],
                    backgroundColor: [
                        'rgba(229, 62, 62, 0.8)',
                        'rgba(221, 107, 32, 0.8)',
                        'rgba(214, 158, 46, 0.8)',
                        'rgba(56, 161, 105, 0.8)',
                        'rgba(49, 130, 206, 0.8)'
                    ]
                }]
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                    title: {
                        display: true,
                        text: 'Overall Severity Distribution'
                    }
                }
            }
        });

        // Scan Results Scatter Chart (more meaningful)
        const scatterCtx = document.getElementById('vulnTypeScatterChart').getContext('2d');
        new Chart(scatterCtx, {
            type: 'scatter',
            data: {
                datasets: [
                    ${data.scans.map((scan, index) => {
                        const totalVulns = data.severity_analysis?.total_by_severity;
                        const scanTotal = (scan as any).result_count || 0;
                        const totalAll = totalVulns ? Object.values(totalVulns).reduce((a, b) => a + b, 0) : 1;
                        return `{
                            label: '${scan.name}',
                            data: [
                                {x: 0, y: ${Math.round(scanTotal * ((totalVulns?.critical || 0) / totalAll))}},
                                {x: 1, y: ${Math.round(scanTotal * ((totalVulns?.high || 0) / totalAll))}},
                                {x: 2, y: ${Math.round(scanTotal * ((totalVulns?.medium || 0) / totalAll))}},
                                {x: 3, y: ${Math.round(scanTotal * ((totalVulns?.low || 0) / totalAll))}},
                                {x: 4, y: ${Math.round(scanTotal * ((totalVulns?.info || 0) / totalAll))}}
                            ],
                            backgroundColor: 'rgba(${102 + index * 50}, ${126 + index * 30}, ${234 - index * 40}, 0.6)',
                            borderColor: 'rgba(${102 + index * 50}, ${126 + index * 30}, ${234 - index * 40}, 1)',
                            borderWidth: 2
                        }`;
                    }).join(', ')}
                ]
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                    title: {
                        display: true,
                        text: 'Severity Distribution by Scan'
                    }
                },
                scales: {
                    x: {
                        type: 'linear',
                        position: 'bottom',
                        ticks: {
                            callback: function(value) {
                                const labels = ['Critical', 'High', 'Medium', 'Low', 'Info'];
                                return labels[value] || '';
                            }
                        }
                    },
                    y: {
                        beginAtZero: true,
                        title: {
                            display: true,
                            text: 'Count'
                        }
                    }
                }
            }
        });

        // Progression Chart (Area Chart)
        const progressionCtx = document.getElementById('progressionChart').getContext('2d');
        new Chart(progressionCtx, {
            type: 'line',
            data: {
                labels: [${data.scans.map(scan => `'${scan.name}'`).join(', ')}],
                datasets: [
                    {
                        label: 'Critical',
                        data: [${data.scans.map(scan => {
                            const totalVulns = data.severity_analysis?.total_by_severity;
                            const scanTotal = (scan as any).result_count || 0;
                            const totalAll = totalVulns ? Object.values(totalVulns).reduce((a, b) => a + b, 0) : 1;
                            const criticalRatio = (totalVulns?.critical || 0) / totalAll;
                            return Math.round(scanTotal * criticalRatio);
                        }).join(', ')}],
                        backgroundColor: 'rgba(229, 62, 62, 0.3)',
                        borderColor: 'rgba(229, 62, 62, 1)',
                        fill: true
                    },
                    {
                        label: 'High',
                        data: [${data.scans.map(scan => {
                            const totalVulns = data.severity_analysis?.total_by_severity;
                            const scanTotal = (scan as any).result_count || 0;
                            const totalAll = totalVulns ? Object.values(totalVulns).reduce((a, b) => a + b, 0) : 1;
                            const highRatio = (totalVulns?.high || 0) / totalAll;
                            return Math.round(scanTotal * highRatio);
                        }).join(', ')}],
                        backgroundColor: 'rgba(221, 107, 32, 0.3)',
                        borderColor: 'rgba(221, 107, 32, 1)',
                        fill: true
                    },
                    {
                        label: 'Medium',
                        data: [${data.scans.map(scan => {
                            const totalVulns = data.severity_analysis?.total_by_severity;
                            const scanTotal = (scan as any).result_count || 0;
                            const totalAll = totalVulns ? Object.values(totalVulns).reduce((a, b) => a + b, 0) : 1;
                            const mediumRatio = (totalVulns?.medium || 0) / totalAll;
                            return Math.round(scanTotal * mediumRatio);
                        }).join(', ')}],
                        backgroundColor: 'rgba(214, 158, 46, 0.3)',
                        borderColor: 'rgba(214, 158, 46, 1)',
                        fill: true
                    },
                    {
                        label: 'Low',
                        data: [${data.scans.map(scan => {
                            const totalVulns = data.severity_analysis?.total_by_severity;
                            const scanTotal = (scan as any).result_count || 0;
                            const totalAll = totalVulns ? Object.values(totalVulns).reduce((a, b) => a + b, 0) : 1;
                            const lowRatio = (totalVulns?.low || 0) / totalAll;
                            return Math.round(scanTotal * lowRatio);
                        }).join(', ')}],
                        backgroundColor: 'rgba(56, 161, 105, 0.3)',
                        borderColor: 'rgba(56, 161, 105, 1)',
                        fill: true
                    }
                ]
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                    title: {
                        display: true,
                        text: 'Vulnerability Progression Over Time'
                    }
                },
                scales: {
                    y: {
                        stacked: true,
                        beginAtZero: true
                    }
                }
            }
        });
    `;
  };

  const downloadReport = () => {
    const blob = new Blob([reportHtml], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `scan-comparison-report-${new Date().toISOString().split('T')[0]}.html`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="scan-comparison-modal">
        <div className="scan-comparison-content">
          <div className="loading-container">
            <i className="fas fa-spinner fa-spin" style={{ fontSize: '2rem', color: 'var(--color-primary-500)' }}></i>
            <p>Analyzing scans and generating comparison...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="scan-comparison-modal">
        <div className="scan-comparison-content">
          <div className="error-container">
            <i className="fas fa-exclamation-triangle" style={{ fontSize: '2rem', color: 'var(--color-error)' }}></i>
            <p>{error}</p>
            <button onClick={onClose} className="btn btn-primary">Close</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="scan-comparison-modal">
      <div className="scan-comparison-content">
        <div className="comparison-header">
          <h2>
            <i className="fas fa-chart-bar"></i>
            Scan Comparison Report
          </h2>
          <button onClick={onClose} className="close-btn">
            <i className="fas fa-times"></i>
          </button>
        </div>

        <div className="comparison-actions">
          <button 
            onClick={() => setShowPreview(!showPreview)}
            className="btn btn-secondary"
          >
            <i className="fas fa-eye"></i>
            {showPreview ? 'Hide Preview' : 'Show Preview'}
          </button>
          <button 
            onClick={downloadReport}
            className="btn btn-primary"
          >
            <i className="fas fa-download"></i>
            Download Report
          </button>
        </div>

        {showPreview && (
          <div className="report-preview">
            <iframe
              srcDoc={reportHtml}
              style={{
                width: '100%',
                height: '600px',
                border: '1px solid var(--color-border-primary)',
                borderRadius: '8px'
              }}
              title="Scan Comparison Report Preview"
            />
          </div>
        )}

        {comparisonData && !showPreview && (
          <div className="comparison-summary">
            <div className="summary-grid">
              <div className="summary-card">
                <h3>Scans Compared</h3>
                <div className="summary-value">{comparisonData.scans.length}</div>
              </div>
              <div className="summary-card">
                <h3>Total Targets</h3>
                <div className="summary-value">
                  {Object.keys(comparisonData.target_analysis?.target_metrics || {}).length}
                </div>
              </div>
              <div className="summary-card">
                <h3>Common Targets</h3>
                <div className="summary-value">{comparisonData.target_analysis?.common_targets?.length || 0}</div>
              </div>
              <div className="summary-card">
                <h3>Total Vulnerabilities</h3>
                <div className="summary-value">
                  {comparisonData.severity_analysis?.total_by_severity ?
                    Object.values(comparisonData.severity_analysis.total_by_severity).reduce((sum, count) => sum + count, 0) : 0
                  }
                </div>
              </div>
            </div>

            <div className="conclusion-preview">
              <h3>Key Insights</h3>
              {(comparisonData.conclusions || []).map((conclusion, index) => (
                <p key={index}>• {conclusion}</p>
              ))}
            </div>

            {/* AI-powered comparison narrative (available for any two scans) */}
            {scanIds.length === 2 && aiStatus !== 'idle' && (
              <div className="ai-comparison-section" style={{ marginTop: '2rem' }}>
                <h3 style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem' }}>
                  <i className="fas fa-robot" style={{ color: 'var(--color-purple-text)' }}></i>
                  AI Analysis
                </h3>
                {aiStatus === 'ready' && (
                  <div style={{ whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>{aiText}</div>
                )}
                {(aiStatus === 'loading' || aiStatus === 'generating') && (
                  <p style={{ opacity: 0.8 }}>
                    <i className="fas fa-spinner fa-spin" style={{ marginRight: '0.5rem' }}></i>
                    {aiStatus === 'generating' ? 'Generating AI comparison — this can take up to a minute…' : 'Loading AI analysis…'}
                  </p>
                )}
                {aiStatus === 'unavailable' && (
                  <p style={{ opacity: 0.7 }}>AI analysis is not available for this comparison (AI may be disabled for this client, or generation timed out).</p>
                )}
              </div>
            )}

            {/* Vulnerability Diff Section */}
            {comparisonData.vuln_diff_analysis && (
              <div className="vuln-diff-section" style={{ marginTop: '2rem' }}>
                <h3 style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem' }}>
                  <i className="fas fa-exchange-alt" style={{ color: 'var(--color-purple-text)' }}></i>
                  Vulnerability Changes
                  <span style={{
                    marginLeft: 'auto',
                    padding: '0.25rem 0.75rem',
                    borderRadius: '999px',
                    fontSize: '0.75rem',
                    fontWeight: '600',
                    backgroundColor: comparisonData.vuln_diff_analysis.summary.trend_direction === 'improving' ? 'var(--color-tint-green)' :
                      comparisonData.vuln_diff_analysis.summary.trend_direction === 'worsening' ? 'var(--color-tint-red)' : 'var(--color-background-tertiary)',
                    color: comparisonData.vuln_diff_analysis.summary.trend_direction === 'improving' ? 'var(--color-success-dark)' :
                      comparisonData.vuln_diff_analysis.summary.trend_direction === 'worsening' ? 'var(--color-critical)' : 'var(--color-text-secondary)'
                  }}>
                    <i className={`fas ${
                      comparisonData.vuln_diff_analysis.summary.trend_direction === 'improving' ? 'fa-arrow-down' :
                      comparisonData.vuln_diff_analysis.summary.trend_direction === 'worsening' ? 'fa-arrow-up' : 'fa-minus'
                    }`} style={{ marginRight: '0.25rem' }}></i>
                    {comparisonData.vuln_diff_analysis.summary.trend_direction === 'improving' ? 'Improving' :
                     comparisonData.vuln_diff_analysis.summary.trend_direction === 'worsening' ? 'Worsening' : 'Stable'}
                  </span>
                </h3>

                {/* Diff Summary Cards */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem', marginBottom: '1.5rem' }}>
                  <div style={{
                    padding: '1rem',
                    borderRadius: '8px',
                    backgroundColor: 'var(--color-tint-red)',
                    borderLeft: '4px solid var(--color-critical)'
                  }}>
                    <div style={{ fontSize: '1.5rem', fontWeight: '700', color: 'var(--color-critical)' }}>
                      +{comparisonData.vuln_diff_analysis.summary.total_new}
                    </div>
                    <div style={{ fontSize: '0.875rem', color: 'var(--color-critical)' }}>New Vulnerabilities</div>
                  </div>
                  <div style={{
                    padding: '1rem',
                    borderRadius: '8px',
                    backgroundColor: 'var(--color-tint-green)',
                    borderLeft: '4px solid var(--color-success-dark)'
                  }}>
                    <div style={{ fontSize: '1.5rem', fontWeight: '700', color: 'var(--color-success-dark)' }}>
                      -{comparisonData.vuln_diff_analysis.summary.total_resolved}
                    </div>
                    <div style={{ fontSize: '0.875rem', color: 'var(--color-success-dark)' }}>Resolved</div>
                  </div>
                  <div style={{
                    padding: '1rem',
                    borderRadius: '8px',
                    backgroundColor: 'var(--color-background-tertiary)',
                    borderLeft: '4px solid var(--color-text-tertiary)'
                  }}>
                    <div style={{ fontSize: '1.5rem', fontWeight: '700', color: 'var(--color-text-secondary)' }}>
                      {comparisonData.vuln_diff_analysis.summary.total_persistent}
                    </div>
                    <div style={{ fontSize: '0.875rem', color: 'var(--color-text-tertiary)' }}>Persistent</div>
                  </div>
                </div>

                {/* New Vulnerabilities List */}
                {comparisonData.vuln_diff_analysis.new_vulnerabilities.length > 0 && (
                  <div style={{ marginBottom: '1.5rem' }}>
                    <h4 style={{ color: 'var(--color-critical)', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: '600' }}>
                      <i className="fas fa-plus-circle" style={{ marginRight: '0.5rem' }}></i>
                      New Vulnerabilities ({comparisonData.vuln_diff_analysis.new_vulnerabilities.length})
                    </h4>
                    <div style={{ maxHeight: '200px', overflowY: 'auto', border: '1px solid var(--color-tint-red)', borderRadius: '8px' }}>
                      {comparisonData.vuln_diff_analysis.new_vulnerabilities.slice(0, 10).map((vuln, idx) => (
                        <div key={idx} style={{
                          padding: '0.75rem',
                          borderBottom: '1px solid var(--color-tint-red)',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.75rem',
                          backgroundColor: idx % 2 === 0 ? 'var(--color-tint-red)' : 'var(--color-background)'
                        }}>
                          <span style={{
                            padding: '0.125rem 0.5rem',
                            borderRadius: '4px',
                            fontSize: '0.65rem',
                            fontWeight: '600',
                            backgroundColor: vuln.severity === 'critical' ? 'var(--color-critical)' :
                              vuln.severity === 'high' ? 'var(--color-critical)' :
                              vuln.severity === 'medium' ? 'var(--color-warning-dark)' :
                              vuln.severity === 'low' ? 'var(--color-success-dark)' : 'var(--color-primary-500)',
                            color: 'var(--color-text-inverse)',
                            textTransform: 'uppercase'
                          }}>{vuln.severity}</span>
                          <span style={{ flex: 1, fontSize: '0.875rem', color: 'var(--color-text-primary)' }}>{vuln.title}</span>
                          <span style={{ fontSize: '0.75rem', color: 'var(--color-text-tertiary)' }}>{vuln.host}:{vuln.port}</span>
                        </div>
                      ))}
                      {comparisonData.vuln_diff_analysis.new_vulnerabilities.length > 10 && (
                        <div style={{ padding: '0.5rem', textAlign: 'center', color: 'var(--color-text-tertiary)', fontSize: '0.75rem' }}>
                          ... and {comparisonData.vuln_diff_analysis.new_vulnerabilities.length - 10} more
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Resolved Vulnerabilities List */}
                {comparisonData.vuln_diff_analysis.resolved_vulnerabilities.length > 0 && (
                  <div>
                    <h4 style={{ color: 'var(--color-success-dark)', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: '600' }}>
                      <i className="fas fa-check-circle" style={{ marginRight: '0.5rem' }}></i>
                      Resolved Vulnerabilities ({comparisonData.vuln_diff_analysis.resolved_vulnerabilities.length})
                    </h4>
                    <div style={{ maxHeight: '200px', overflowY: 'auto', border: '1px solid var(--color-tint-green)', borderRadius: '8px' }}>
                      {comparisonData.vuln_diff_analysis.resolved_vulnerabilities.slice(0, 10).map((vuln, idx) => (
                        <div key={idx} style={{
                          padding: '0.75rem',
                          borderBottom: '1px solid var(--color-tint-green)',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.75rem',
                          backgroundColor: idx % 2 === 0 ? 'var(--color-tint-green)' : 'var(--color-background)'
                        }}>
                          <span style={{
                            padding: '0.125rem 0.5rem',
                            borderRadius: '4px',
                            fontSize: '0.65rem',
                            fontWeight: '600',
                            backgroundColor: vuln.severity === 'critical' ? 'var(--color-critical)' :
                              vuln.severity === 'high' ? 'var(--color-critical)' :
                              vuln.severity === 'medium' ? 'var(--color-warning-dark)' :
                              vuln.severity === 'low' ? 'var(--color-success-dark)' : 'var(--color-primary-500)',
                            color: 'var(--color-text-inverse)',
                            textTransform: 'uppercase'
                          }}>{vuln.severity}</span>
                          <span style={{ flex: 1, fontSize: '0.875rem', color: 'var(--color-text-primary)', textDecoration: 'line-through', opacity: 0.7 }}>{vuln.title}</span>
                          <span style={{ fontSize: '0.75rem', color: 'var(--color-text-tertiary)' }}>{vuln.host}:{vuln.port}</span>
                        </div>
                      ))}
                      {comparisonData.vuln_diff_analysis.resolved_vulnerabilities.length > 10 && (
                        <div style={{ padding: '0.5rem', textAlign: 'center', color: 'var(--color-text-tertiary)', fontSize: '0.75rem' }}>
                          ... and {comparisonData.vuln_diff_analysis.resolved_vulnerabilities.length - 10} more
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ScanComparison;